"use client"

import React, { useEffect, useState } from "react"
import { usePathname, useSearchParams } from "next/navigation"

import { cn } from "@/lib/utils"
import { Navigation } from "@/components/ui/organisms/navigation/Navigation"
import { TNavButtons, TNavItem } from "@/components/ui/organisms"

type TNavHeaderProps = {
  className?: string
  mainNav: TNavItem[]
  navButtons: TNavButtons[]
}

const NavHeader = ({ className, mainNav, navButtons }: TNavHeaderProps) => {
  const [menuOpen, setMenuOpen] = useState(false)

  const pathname = usePathname()
  const searchParams = useSearchParams()

  // Auto-close the menu on route change. Adjusting state while rendering
  // (rather than in an effect) avoids an extra commit: React applies this
  // change before painting instead of rendering once, then closing.
  // See https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  const [routeKey, setRouteKey] = useState({ pathname, searchParams })
  if (
    routeKey.pathname !== pathname ||
    routeKey.searchParams !== searchParams
  ) {
    setRouteKey({ pathname, searchParams })
    if (menuOpen) {
      setMenuOpen(false)
    }
  }

  const toggleMenu = () => {
    setMenuOpen((prevState) => !prevState)
  }

  // Effect for handling resize and scroll events
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1280 && menuOpen) {
        toggleMenu()
      }
    }

    const handlePageOverFlow = () => {
      document.body.style.overflow = menuOpen ? "hidden" : "auto"
    }

    window.addEventListener("resize", handleResize)
    handlePageOverFlow()

    return () => {
      window.removeEventListener("resize", handleResize)
      document.body.style.overflow = "auto"
    }
  }, [menuOpen])

  const headerClasses = cn(
    `sticky top-0 z-50 flex w-full flex-col transition-all ease-in-out`,
    { "shadow-100": !menuOpen },
    className
  )
  return (
    <header id="header-nav" className={headerClasses}>
      <Navigation
        navItems={mainNav}
        navButtons={navButtons}
        toggleMenu={toggleMenu}
        isMenuOpen={menuOpen}
      />
    </header>
  )
}

export { NavHeader, type TNavHeaderProps }
